import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Edit, Trash2, Plus, X } from 'lucide-react';
import API_URL from '../config';
import logger from '../utils/logger';


const RolesAndGoals = () => {
  const [rolesAndGoals, setRolesAndGoals] = useState([]);
  const [newRole, setNewRole] = useState('');
  const [newGoal, setNewGoal] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editRole, setEditRole] = useState('');
  const [editGoal, setEditGoal] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);

  useEffect(() => {
    fetchRolesAndGoals();
  }, []);

  const fetchRolesAndGoals = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/roles-and-goals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRolesAndGoals(response.data);
    } catch (error) {
      logger.error('Error fetching roles and goals:', error);
      setError('Failed to fetch roles and goals');
    }
  };

  const handleAddRoleAndGoal = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/roles-and-goals`, 
        { role: newRole, goal: newGoal },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRolesAndGoals([...rolesAndGoals, response.data]);
      setNewRole('');
      setNewGoal('');
      setMessage('Role and Goal added successfully');
      setShowNewForm(false);
    } catch (error) {
      logger.error('Error adding role and goal:', error);
      setError('Failed to add role and goal');
    }
  };

  const handleEditRoleAndGoal = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_URL}/roles-and-goals/${editingId}`, 
        { role: editRole, goal: editGoal },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRolesAndGoals(rolesAndGoals.map(item => item._id === editingId ? response.data : item));
      setEditingId(null);
      setEditRole('');
      setEditGoal('');
      setMessage('Role and Goal updated successfully');
    } catch (error) {
      logger.error('Error updating role and goal:', error);
      setError('Failed to update role and goal');
    }
  };

  const handleDeleteRoleAndGoal = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/roles-and-goals/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRolesAndGoals(rolesAndGoals.filter(item => item._id !== id));
      setMessage('Role and Goal deleted successfully');
    } catch (error) {
      logger.error('Error deleting role and goal:', error);
      setError('Failed to delete role and goal');
    }
  };

  const startEditing = (item) => {
    setEditingId(item._id);
    setEditRole(item.role);
    setEditGoal(item.goal);
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">Quarterly Goals</h1>
      {error && <p className="text-red-500 mb-4">{error}</p>}
      {message && <p className="text-green-500 mb-4">{message}</p>}
      
      <button 
        onClick={() => setShowNewForm(true)}
        className="mb-4 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center"
      >
        <Plus size={20} className="mr-2" />
        Add New Goals
      </button>

      {showNewForm && (
        <form onSubmit={handleAddRoleAndGoal} className="mb-4 bg-white shadow-lg rounded-lg p-4">
          <div className="mb-4">
            <input
              type="text"
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              placeholder="Enter Category"
              className="w-full p-2 border rounded"
              required
            />
          </div>
          <div className="mb-4">
            <input
              type="text"
              value={newGoal}
              onChange={(e) => setNewGoal(e.target.value)}
              placeholder="Enter goal"
              className="w-full p-2 border rounded"
              required
            />
          </div>
          <div className="flex justify-end">
            <button type="submit" className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded mr-2">
              Add
            </button>
            <button type="button" onClick={() => setShowNewForm(false)} className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rolesAndGoals.map((item) => (
          <div key={item._id} className="bg-white shadow-lg rounded-lg p-4">
            {editingId === item._id ? (
              <form onSubmit={handleEditRoleAndGoal} className="space-y-4">
                <input
                  type="text"
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value)}
                  className="w-full p-2 border rounded"
                  required
                />
                <input
                  type="text"
                  value={editGoal}
                  onChange={(e) => setEditGoal(e.target.value)}
                  className="w-full p-2 border rounded"
                  required
                />
                <div className="flex justify-end space-x-2">
                  <button type="submit" className="bg-green-500 hover:bg-green-700 text-white font-bold py-1 px-2 rounded">
                    Save
                  </button>
                  <button type="button" onClick={() => setEditingId(null)} className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-1 px-2 rounded">
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <>
                <h3 className="text-xl font-bold mb-2">{item.role}</h3>
                <p className="text-gray-700 mb-4">{item.goal}</p>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => startEditing(item)}
                    className="text-blue-500 hover:text-blue-700"
                  >
                    <Edit size={20} />
                  </button>
                  <button
                    onClick={() => handleDeleteRoleAndGoal(item._id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default RolesAndGoals;